import { UUID } from "./outings";

export type StayEntry =
  | { action: "new"; place: string | null }
  | { action: "edit"; id: string };
export type StayPlace = {
  slug: string;
  name: string;
  timezone: string | null;
  region: string | null;
};
const slug = /^[a-z0-9][a-z0-9-]{0,159}$/;
export function validStayPlaceSlug(value: unknown): value is string {
  return typeof value === "string" && slug.test(value);
}

/** Only public place slugs and an opaque own-stay ID travel in navigation.
 * A link requests an editor, never a save or a change to the active stay. */
export function parseStayEntry(params: {
  new?: string | string[] | null;
  edit?: string | string[] | null;
  place?: string | string[] | null;
}): StayEntry | null {
  if (params.new === "1" && !params.edit)
    return {
      action: "new",
      place: validStayPlaceSlug(params.place) ? params.place : null,
    };
  if (!params.new && typeof params.edit === "string" && UUID.test(params.edit))
    return { action: "edit", id: params.edit.toLowerCase() };
  return null;
}

export function stayEntryHref(entry: StayEntry | null): string {
  if (!entry) return "/companion/plan/trips";
  const params = new URLSearchParams(
    entry.action === "new"
      ? {
          new: "1",
          ...(validStayPlaceSlug(entry.place) ? { place: entry.place } : {}),
        }
      : UUID.test(entry.id)
        ? { edit: entry.id.toLowerCase() }
        : {},
  );
  return `/companion/plan/trips${params.size ? `?${params}` : ""}`;
}

export function readStayPlaces(value: unknown): StayPlace[] {
  if (
    !value ||
    typeof value !== "object" ||
    !("places" in value) ||
    !Array.isArray(value.places) ||
    value.places.length > 20
  )
    throw new Error(
      "Destinations could not be checked. Please retry the search.",
    );
  const places: unknown[] = value.places;
  if (
    !places.every((p) => {
      if (!p || typeof p !== "object") return false;
      const row = p as Record<string, unknown>;
      return (
        validStayPlaceSlug(row.slug) &&
        typeof row.name === "string" &&
        row.name.length > 0 &&
        row.name.length <= 240 &&
        (row.timezone === null ||
          (typeof row.timezone === "string" && row.timezone.length <= 80)) &&
        (row.region === null ||
          (typeof row.region === "string" && row.region.length <= 240))
      );
    })
  )
    throw new Error(
      "Destinations could not be checked. Please retry the search.",
    );
  return places as StayPlace[];
}
